import { useEffect, useRef } from 'react';

import type { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsContentState } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useReattachComment } from '@confluence/comment-context/entry-points/ReattachCommentContext.ts';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import type { CreateInlineCommentCompoundExperienceEntryPoint } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { getLegacyConvertedSelectionObj } from './getLegacyConvertedSelectionObj.ts';

export const useCreateCommentAction = ({
	contentId,
	isFabric,
	sendKeyboardShortcutAnalytics,
}: {
	contentId: string;
	isFabric: boolean;
	sendKeyboardShortcutAnalytics: (shortcut: string) => void;
}): {
	handleCommentButtonClick: ({
		dialogs,
		shortcut,
		experienceAttributes,
	}: {
		dialogs: DialogsStateContainer;
		shortcut?: string;
		experienceAttributes?: {
			entryPoint: CreateInlineCommentCompoundExperienceEntryPoint;
			edition: ConfluenceEdition | null;
		};
	}) => void;
} => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const { setSelectionOptionsForCreateEditor } = useAnnotationsDispatch();
	const { createComment } = useInlineCommentsDispatchContext();
	const { commentToReattach } = useReattachComment();
	const { hasContentChanged } = useCommentsContentState();

	const content = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		content.current = document.querySelector('div#content');
	}, []);

	const createReactInlineCommentHighlight = ({
		experienceAttributes,
	}: {
		experienceAttributes?: {
			entryPoint: CreateInlineCommentCompoundExperienceEntryPoint;
			edition: ConfluenceEdition | null;
		};
	} = {}) => {
		if (isObjectSidebarShown) {
			hideObjectSidebar();
		}
		if (isFabric) {
			setSelectionOptionsForCreateEditor({ experienceAttributes });
		} else {
			if (content.current) {
				createComment(
					getLegacyConvertedSelectionObj({
						content,
						contentId,
						commentToReattach,
					}),
				);
			}
		}
	};

	const handleCommentButtonClick = ({
		dialogs,
		shortcut,
		experienceAttributes,
	}: {
		dialogs: DialogsStateContainer;
		shortcut?: string;
		experienceAttributes?: {
			entryPoint: CreateInlineCommentCompoundExperienceEntryPoint;
			edition: ConfluenceEdition | null;
		};
	}): void => {
		if (hasContentChanged) {
			dialogs.showModal(CommentWarningDialog, {
				onConfirm: () => createReactInlineCommentHighlight({ experienceAttributes }),
			});
		} else {
			createReactInlineCommentHighlight({ experienceAttributes });
		}

		if (shortcut) {
			sendKeyboardShortcutAnalytics(shortcut);
		}
	};

	return { handleCommentButtonClick };
};
